import { AudioHighlightMetadata } from '@modules/feedback/components/AudioType/types';
import {
  getCurrentPlayingMessageIndex,
  getHighlightForSelectedLabel,
  highlightPlayingAudioTranscriptMessage,
  scrollToPlayingAudioTranscriptMessageIfRequired,
} from '@modules/feedback/components/AudioType/utils';
import useFeedbackListStore, {
  FeedbackListStore,
} from '@modules/feedback/store/useFeedbackListStore';
import { useEffect } from 'react';
import { usePrevious, useUpdateEffect } from 'react-use';
import { FeedbackLabel } from 'v2/core/entities/feedback';
import { AudioConversationMessage } from 'v2/core/entities/feedback/content';
import { UUID } from 'v2/core/entities/uuid';
import { shallow } from 'zustand/shallow';

const getFeedbackListStoreState = (
  state: FeedbackListStore,
  feedbackID: UUID,
) => {
  return {
    setSelectedSeekJumpTime: state.setSelectedSeekJumpTime,
    setCurrentPlayingMsgIndex: state.setCurrentPlayingMsgIndex,
    setAudioHighlightLabelID: state.setAudioHighlightLabelID,
    setIsTranscriptPopoverExpanded: state.setIsTranscriptPopoverExpanded,
    duration: state.feedbacks?.[feedbackID]?.audioDuration ?? 0,
    seek: state.feedbacks?.[feedbackID]?.audioSeek ?? 0,
    selectedSeekJumpTime: state.feedbacks?.[feedbackID]?.selectedSeekJumpTime,
    snippetTimeToPlay: state.feedbacks?.[feedbackID]?.snippetTimeToPlay,
    isTranscriptPopoverExpanded:
      state.feedbacks[feedbackID]?.isTranscriptPopoverExpanded ?? false,
    audioHighlightLabelID: state.feedbacks?.[feedbackID]?.audioHighlightLabelID,
  };
};

interface AudioFeedbackTranscriptControlsParams {
  feedbackID: UUID;
  isFeedbackCardView: boolean;
  isSeeking: boolean;
  isPlaying: boolean;
  highlightMetadata: AudioHighlightMetadata[];
  selectedLabels: FeedbackLabel[];
  conversation: AudioConversationMessage[];
  onJumpSeekWithBuffer: (time: number) => void;
  onJumpSeek: (time: number) => void;
  onTogglePlay: (options?: { shouldSyncSeekValue: boolean }) => void;
  isEmptyAudioSource: boolean;
}

export const useAudioFeedbackTranscriptControls = ({
  feedbackID,
  isFeedbackCardView,
  isSeeking,
  isPlaying,
  selectedLabels,
  highlightMetadata,
  conversation,
  onJumpSeek,
  onTogglePlay,
  isEmptyAudioSource,
}: AudioFeedbackTranscriptControlsParams) => {
  const {
    seek,
    selectedSeekJumpTime,
    snippetTimeToPlay,
    isTranscriptPopoverExpanded,
    audioHighlightLabelID,
    setSelectedSeekJumpTime,
    setCurrentPlayingMsgIndex,
    setAudioHighlightLabelID,
  } = useFeedbackListStore(
    (state) => getFeedbackListStoreState(state, feedbackID),
    shallow,
  );

  const queryParams = new URLSearchParams(window.location.search);
  const startTimeQueryParam = queryParams.get('start');
  const endTimeQueryParam = queryParams.get('end');
  const isSharedSnippetView = Boolean(startTimeQueryParam && endTimeQueryParam);

  const prevHighlightMetadata = usePrevious(highlightMetadata);

  const isSnippetTimeToPlayEmpty = !snippetTimeToPlay;

  useEffect(() => {
    // highlight & scroll to current playing message bubble
    if (isSeeking) {
      return;
    }
    const currentPlayingMsgIndex = getCurrentPlayingMessageIndex(
      conversation,
      seek,
    );
    setCurrentPlayingMsgIndex(currentPlayingMsgIndex, feedbackID);
    setTimeout(() => {
      scrollToPlayingAudioTranscriptMessageIfRequired(currentPlayingMsgIndex);
    }, 100); // wait for transcript to render

    if (isSnippetTimeToPlayEmpty && !isEmptyAudioSource) {
      highlightPlayingAudioTranscriptMessage(currentPlayingMsgIndex);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isSnippetTimeToPlayEmpty,
    isSeeking,
    conversation,
    seek,
    isFeedbackCardView,
    isTranscriptPopoverExpanded,
    isEmptyAudioSource,
  ]);

  useEffect(() => {
    // jump & play audio from transcript/summary/shared snippet
    if (selectedSeekJumpTime === undefined) {
      return;
    }

    onJumpSeek(selectedSeekJumpTime);
    setSelectedSeekJumpTime(undefined, feedbackID);

    if (!isPlaying) {
      onTogglePlay({ shouldSyncSeekValue: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedbackID, isPlaying, selectedSeekJumpTime]);

  useUpdateEffect(() => {
    // set 'seek' state from first default prediction highlight
    if (
      !isFeedbackCardView ||
      highlightMetadata.length === 0 ||
      Boolean(prevHighlightMetadata?.length) ||
      isEmptyAudioSource
    ) {
      return;
    }

    const seekTime = getHighlightForSelectedLabel(
      highlightMetadata,
      selectedLabels,
    )?.startTime;

    if (seekTime !== undefined) {
      onJumpSeek(seekTime);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    feedbackID,
    highlightMetadata,
    isFeedbackCardView,
    prevHighlightMetadata?.length,
    selectedLabels,
    isEmptyAudioSource,
  ]);

  useUpdateEffect(() => {
    // jump audio to first selected prediction highlight
    if (
      isFeedbackCardView ||
      !audioHighlightLabelID ||
      highlightMetadata.length === 0
    ) {
      return;
    }

    if (isSharedSnippetView || isPlaying) {
      setAudioHighlightLabelID(undefined, feedbackID);
      return;
    }

    const seekTime = getHighlightForSelectedLabel(
      highlightMetadata,
      selectedLabels,
    )?.startTime;

    if (seekTime !== undefined) {
      onJumpSeek(seekTime);
      setAudioHighlightLabelID(undefined, feedbackID);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isSharedSnippetView,
    highlightMetadata,
    selectedLabels,
    isFeedbackCardView,
  ]);
};
